import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip } from "recharts";
import { computeIdrComparison } from "@/lib/idrScoring";
import { downloadElementAsPdf } from "@/lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const IDR_ENTOURAGE_KEY = "m2hat_idr_entourage_token";
const PROCHE_COLORS = ["#8A9A6D", "#B0553B", "#5A7D9A", "#A05A8A", "#C08552"];
const TICK_INK = { fill: "#6E5C53", fontSize: 12 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };

const T = {
  fr: {
    inviteTitle: "Le regard d'un proche",
    inviteText: "Envoyez ce lien à un proche. Il répondra aux mêmes 50 affirmations, mais en pensant à vous. Vous seul(e) verrez la comparaison, une fois votre inventaire terminé.",
    createBtn: "Créer le lien à partager",
    creating: "Création…",
    createError: "Impossible de créer le lien — réessayez.",
    linkLabel: "Lien à partager avec votre proche",
    copy: "Copier le lien",
    copied: "Lien copié",
    waiting: "En attente de la réponse de votre proche… La comparaison s'affichera ici. Gardez ce lien pour revenir plus tard.",
    inviteMore: "Vous pouvez inviter d'autres proches en partageant à nouveau ce lien.",
    respondedOne: "proche a répondu.",
    respondedMany: "proches ont répondu.",
    comparisonTitle: "Comparaison avec le regard de vos proches",
    comparisonSub: "Votre auto-évaluation comparée à la perception de vos proches (0 – 100 %)",
    you: "Vous",
    proche: "Proche",
    exportPdf: "Télécharger la comparaison (PDF)",
    exporting: "Génération du PDF…",
    pdfName: "comparaison-idr-entourage.pdf",
    gapTitle: "Là où vos regards divergent le plus",
    gapText: (name, patient, avg, gap, multi) =>
      `La divergence la plus marquée concerne le driver « ${name} » : vous l'évaluez à ${patient} %, tandis que ${multi ? "vos proches le situent en moyenne" : "votre proche le situe"} à ${avg} % (écart de ${gap} points). C'est un point de dialogue privilégié.`,
    convergentText: (name) =>
      `Vos regards sont globalement convergents : aucun driver ne présente d'écart marqué. La plus légère différence porte sur « ${name} ».`,
    dialogueTitle: "Pour en parler ensemble",
    dialogue: [
      "Sur quel driver votre regard et celui de votre proche diffèrent-ils le plus ? Qu'est-ce que cela vous apprend ?",
      "Y a-t-il un driver que votre proche perçoit plus fort chez vous que vous ne le ressentez ?",
      "Qu'est-ce qui vous a surpris dans le regard de votre proche ?",
      "Quel premier petit pas aimeriez-vous poser, à la lumière de cet échange ?",
    ],
  },
  es: {
    inviteTitle: "La mirada de un allegado",
    inviteText: "Envíe este enlace a una persona cercana. Responderá a las mismas 50 afirmaciones, pero pensando en usted. Solo usted verá la comparación, una vez terminado su inventario.",
    createBtn: "Crear el enlace para compartir",
    creating: "Creando…",
    createError: "No se pudo crear el enlace — inténtelo de nuevo.",
    linkLabel: "Enlace para compartir con su allegado",
    copy: "Copiar el enlace",
    copied: "Enlace copiado",
    waiting: "Esperando la respuesta de su allegado… La comparación aparecerá aquí. Guarde este enlace para volver más tarde.",
    inviteMore: "Puede invitar a más allegados compartiendo de nuevo este enlace.",
    respondedOne: "allegado ha respondido.",
    respondedMany: "allegados han respondido.",
    comparisonTitle: "Comparación con la mirada de sus allegados",
    comparisonSub: "Su autoevaluación comparada con la percepción de sus allegados (0 – 100 %)",
    you: "Usted",
    proche: "Allegado",
    exportPdf: "Descargar la comparación (PDF)",
    exporting: "Generando el PDF…",
    pdfName: "comparacion-idr-allegados.pdf",
    gapTitle: "Donde más difieren sus miradas",
    gapText: (name, patient, avg, gap, multi) =>
      `La mayor divergencia se refiere al driver «${name}»: usted lo evalúa en ${patient} %, mientras que ${multi ? "sus allegados lo sitúan en promedio" : "su allegado lo sitúa"} en ${avg} % (diferencia de ${gap} puntos). Es un punto de diálogo privilegiado.`,
    convergentText: (name) =>
      `Sus miradas son globalmente convergentes: ningún driver presenta una diferencia marcada. La diferencia más leve se refiere a «${name}».`,
    dialogueTitle: "Para hablarlo juntos",
    dialogue: [
      "¿En qué driver difieren más su mirada y la de su allegado? ¿Qué le enseña esto?",
      "¿Hay algún driver que su allegado percibe más fuerte en usted de lo que usted siente?",
      "¿Qué le sorprendió de la mirada de su allegado?",
      "¿Qué primer pequeño paso le gustaría dar a la luz de este intercambio?",
    ],
  },
};

const ScoreLine = ({ label, value, color }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-[var(--ink)]">{label}</span>
      <span className="font-semibold" style={{ color }}>{value} %</span>
    </div>
    <div className="h-2 w-full rounded-full bg-[var(--beige)] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
  </div>
);

export const IdrEntourage = ({ variant = "full", patientAnswers = null, lang = "fr" }) => {
  const t = T[lang];
  const [token, setToken] = useState(() => localStorage.getItem(IDR_ENTOURAGE_KEY) || "");
  const [proches, setProches] = useState([]);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);
  const hasAnswers = !!patientAnswers && Object.keys(patientAnswers).length >= 50;
  const [procheToken, setProcheToken] = useState("");
  const shareUrl = procheToken ? `${window.location.origin}/p/idr-entourage/${procheToken}` : "";

  useEffect(() => {
    if (!token || !hasAnswers) return;
    axios.post(`${API}/idr-entourage/seances/${token}/patient`, { patient_answers: patientAnswers }).catch(() => {});
  }, [token, hasAnswers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    let active = true;
    const poll = async () => {
      try {
        const data = await axios.get(`${API}/idr-entourage/seances/${token}`).then((r) => r.data);
        if (!active || !data.found) return;
        setProches(Array.isArray(data.proches) ? data.proches : []);
        if (data.proche_token) setProcheToken(data.proche_token);
      } catch { /* réseau */ }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { active = false; clearInterval(id); };
  }, [token]);

  const create = async () => {
    setCreating(true);
    try {
      const out = await axios.post(`${API}/idr-entourage/seances`, { lang, patient_answers: hasAnswers ? patientAnswers : {} }).then((r) => r.data);
      localStorage.setItem(IDR_ENTOURAGE_KEY, out.token);
      setProcheToken(out.proche_token || "");
      setToken(out.token);
    } catch { toast.error(t.createError); }
    setCreating(false);
  };

  const copy = () => navigator.clipboard.writeText(shareUrl).then(() => toast.success(t.copied)).catch(() => {});

  const inviteCard = (
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 no-print" data-testid="idr-entourage-invite">
      <div className="text-center mb-4">
        <h3 className="font-serif-title text-2xl sm:text-3xl text-[var(--gold)] mb-2">{t.inviteTitle}</h3>
        <p className="text-sm text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.inviteText}</p>
      </div>
      {!token ? (
        <div className="text-center">
          <button onClick={create} disabled={creating} data-testid="idr-entourage-create" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">{creating ? t.creating : t.createBtn}</button>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-xs text-[var(--ink-soft)] mb-2">{t.linkLabel}</p>
          <div className="rounded-xl bg-white/80 border border-[var(--border)] p-3 text-sm text-[var(--ink)] break-all mb-4" data-testid="idr-entourage-link">{shareUrl}</div>
          <div className="flex justify-center my-4">
            <div className="bg-white p-3 rounded-2xl border border-[var(--border)]">
              {shareUrl && <QRCodeSVG value={shareUrl} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" />}
            </div>
          </div>
          <button onClick={copy} data-testid="idr-entourage-copy" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.copy}</button>
          <p className="text-xs text-[var(--ink-soft)] mt-4">{proches.length > 0 ? t.inviteMore : t.waiting}</p>
        </div>
      )}
    </div>
  );

  if (variant === "invite") {
    return <div className="mb-8">{inviteCard}</div>;
  }

  const showComparison = token && proches.length > 0 && hasAnswers;
  const comp = showComparison ? computeIdrComparison(patientAnswers, proches, lang) : null;
  const gapInfo = comp ? (() => {
    const multi = proches.length > 1;
    const rows = comp.rows.map((r) => {
      const avg = r.proches.length ? Math.round(r.proches.reduce((a, b) => a + b, 0) / r.proches.length) : 0;
      return { name: r.name, patient: r.patient, avg, gap: Math.abs(r.patient - avg) };
    });
    const top = rows.reduce((a, b) => (b.gap > a.gap ? b : a), rows[0]);
    return { ...top, multi };
  })() : null;

  const doExport = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      await downloadElementAsPdf(printRef.current, t.pdfName);
    } catch {
      toast.error(lang === "es" ? "No se pudo generar el PDF." : "Impossible de générer le PDF.");
    }
    setExporting(false);
  };

  return (
    <div className="space-y-6 print-container" data-testid="idr-entourage-panel">
      {inviteCard}
      {token && proches.length > 0 && (
        <p className="text-center text-sm text-[var(--ink-soft)] no-print" data-testid="idr-entourage-count">
          {proches.length} {proches.length > 1 ? t.respondedMany : t.respondedOne}
        </p>
      )}
      {showComparison && (
        <>
          <div ref={printRef} className="space-y-6 bg-white p-4 sm:p-6 rounded-3xl">
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 print-break" data-testid="idr-entourage-comparison">
              <div className="text-center mb-6">
                <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{t.comparisonTitle}</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-1">{t.comparisonSub}</p>
              </div>
              <ResponsiveContainer width="100%" height={380}>
                <RadarChart data={comp.radarData} outerRadius="70%">
                  <PolarGrid stroke="#d8ccbe" />
                  <PolarAngleAxis dataKey="subject" tick={TICK_INK} />
                  <PolarRadiusAxis domain={[0, 100]} tick={TICK_MUTED} angle={90} />
                  <Radar name={t.you} dataKey="patient" stroke="#B58C67" fill="#B58C67" fillOpacity={0.4} />
                  {proches.map((pr, j) => (
                    <Radar key={j} name={pr.name || `${t.proche} ${j + 1}`} dataKey={`proche${j}`} stroke={PROCHE_COLORS[j % PROCHE_COLORS.length]} fill={PROCHE_COLORS[j % PROCHE_COLORS.length]} fillOpacity={0.15} />
                  ))}
                  <Legend />
                  <Tooltip formatter={(v) => `${v} %`} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print-break">
              {comp.rows.map((row) => (
                <div key={row.key} className="rounded-2xl bg-white/70 border border-[var(--border)] p-4" data-testid={`idr-entourage-row-${row.key}`}>
                  <h4 className="font-serif-title text-xl mb-3" style={{ color: row.color }}>{row.name}</h4>
                  <div className="space-y-2">
                    <ScoreLine label={t.you} value={row.patient} color="#B58C67" />
                    {row.proches.map((v, j) => (
                      <ScoreLine key={j} label={proches[j].name || `${t.proche} ${j + 1}`} value={v} color={PROCHE_COLORS[j % PROCHE_COLORS.length]} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {gapInfo && (
              <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-7 print-break" data-testid="idr-entourage-gap">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/85 mb-3">{t.gapTitle}</p>
                <p className="leading-relaxed text-[0.98rem]" data-testid="idr-entourage-gap-text">
                  {gapInfo.gap > 10
                    ? t.gapText(gapInfo.name, gapInfo.patient, gapInfo.avg, gapInfo.gap, gapInfo.multi)
                    : t.convergentText(gapInfo.name)}
                </p>
              </div>
            )}

            <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-6 sm:p-7 print-break" data-testid="idr-entourage-dialogue">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[var(--gold)] mb-3">{t.dialogueTitle}</p>
              <ul className="list-disc pl-5 space-y-2 text-sm text-[var(--ink)] leading-relaxed">
                {t.dialogue.map((d, i) => (<li key={i}>{d}</li>))}
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 no-print">
            <button onClick={doExport} disabled={exporting} data-testid="idr-entourage-export" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">{exporting ? t.exporting : t.exportPdf}</button>
          </div>
        </>
      )}
    </div>
  );
};
